// Material-change classification (plan §10.8).
//
// This is the trust mechanism of the whole product. A helper who agreed to drive
// Ann on Tuesday 09:15–13:00 must never be quietly assumed to still be fine once
// it moves to Wednesday. Everything downstream — the revision bump, the flip to
// "Please recheck", the impact summary the editor sees before saving — hangs off
// the decision made here.
//
// The policy is an ENUMERATED, FIELD-BY-FIELD table taken from plan §10.8, not a
// heuristic and not a diff size. Product behaviour has to be auditable: somebody
// must be able to read this file and say exactly which edits will disturb
// people, without running it.

import type { EventRecord } from "../domain/event.js";
import type { SupportNeed } from "../domain/need.js";
import type { HelpMode } from "../domain/enums.js";

/**
 * How one changed field bears on somebody who already agreed to help.
 *
 * Three values, not two. The plan's table has a genuine third case — a changed
 * contact telephone number is marked **Contextual**: "ask editor whether
 * responsible people must recheck". Collapsing that to a boolean would force a
 * wrong default in both directions: silently disturbing everybody, or silently
 * disturbing nobody when the number was the only way to reach the ward.
 */
export type FieldMateriality = "material" | "cosmetic" | "contextual";

/** Fields of a support need this module knows how to classify. */
export type NeedFieldName =
  | "startsAt"
  | "endsAt"
  | "requiredCapacity"
  | "requiredHelpModes"
  | "needType"
  | "importance"
  | "label"
  | "meetingDetail"
  | "practicalNote"
  | "state";

/** Fields of an event this module knows how to classify. */
export type EventFieldName =
  | "startsAt"
  | "endsAt"
  | "locationLabel"
  | "locationPrivate"
  | "title"
  | "shareTitle"
  | "category"
  | "practicalNote"
  | "state";

/** One field that differs between the before and after payloads. */
export interface FieldChange {
  readonly field: NeedFieldName | EventFieldName;
  readonly materiality: FieldMateriality;
  /**
   * Why, as a code rather than a sentence. `@herefor/product-language` owns the
   * words the editor and the helper read; the kernel owns the fact.
   */
  readonly reason: ChangeReasonCode;
}

export type ChangeReasonCode =
  | "help_window_moved"
  | "event_time_moved"
  | "location_changed"
  | "capacity_changed"
  | "help_modes_changed"
  | "need_type_changed"
  | "cancelled"
  | "reminder_threshold_changed"
  | "wording_changed"
  | "note_changed"
  | "contact_details_changed"
  | "share_title_changed";

/**
 * The verdict on a whole edit.
 *
 * `no_change` is a distinct outcome from `cosmetic`. Saving an identical payload
 * must be able to say "nothing happened" — a cosmetic classification would still
 * invite a revision bump and a log entry for an edit that changed nothing.
 */
export type ChangeClassification =
  | { readonly outcome: "no_change"; readonly changes: readonly FieldChange[] }
  | { readonly outcome: "cosmetic"; readonly changes: readonly FieldChange[] }
  | { readonly outcome: "needs_editor_decision"; readonly changes: readonly FieldChange[] }
  | { readonly outcome: "material"; readonly changes: readonly FieldChange[] };

/** Exactly the support-need fields the materiality policy reads. */
export type NeedChangeInput = Pick<
  SupportNeed,
  | "startsAt"
  | "endsAt"
  | "requiredCapacity"
  | "requiredHelpModes"
  | "needType"
  | "importance"
  | "label"
  | "meetingDetail"
  | "practicalNote"
  | "state"
>;

/** Exactly the event fields the materiality policy reads. */
export type EventChangeInput = Pick<
  EventRecord,
  | "startsAt"
  | "endsAt"
  | "locationLabel"
  | "locationPrivate"
  | "title"
  | "shareTitle"
  | "category"
  | "practicalNote"
  | "state"
>;

/**
 * Classify an edit to a support need (plan §10.8, NED-009).
 *
 * NED-009 fixes the material set exactly: "changing a need window, capacity or
 * mode MUST be material". Everything else on a need is presentation or context.
 *
 * ## The field-by-field policy
 *
 * | Field | Verdict | Why |
 * |---|---|---|
 * | `startsAt` / `endsAt` | **material** | The hours somebody promised. The whole point. |
 * | `requiredCapacity` | **material** | Changes whose responsibility it is; coverage recomputes (plan §10.8) |
 * | `requiredHelpModes` | **material** | Somebody who offered to phone may not be able to drive |
 * | `needType` | **material** | "Visit" becoming "drive" is a different job |
 * | `importance` | cosmetic | NED-006: reminder thresholds ONLY, never clinical inference. It changes when we nudge, not what anybody agreed to do |
 * | `label` | cosmetic | Plan §10.8 "title wording only — usually no" |
 * | `meetingDetail` / `practicalNote` | cosmetic | Plan §10.8 "keep commitments; log change" |
 * | `state` → `cancelled` | **material** | Plan §10.8 cancellation row |
 *
 * `importance` is the one place this classifier is deliberately *less* eager
 * than the bead's one-line summary suggested. The plan's §10.8 table does not
 * list it, and NED-006 restricts it to reminder thresholds — so waking four
 * people with "Please recheck" because an organiser reclassified a visit from
 * ordinary to important would be exactly the spam the policy exists to prevent.
 * It still needs to reschedule reminders; that is Epic 7's job, not consent.
 */
export function classifyNeedChange(
  before: NeedChangeInput,
  after: NeedChangeInput,
): ChangeClassification {
  const changes: FieldChange[] = [];

  if (before.startsAt !== after.startsAt || before.endsAt !== after.endsAt) {
    changes.push({
      field: before.startsAt !== after.startsAt ? "startsAt" : "endsAt",
      materiality: "material",
      reason: "help_window_moved",
    });
  }

  if (before.requiredCapacity !== after.requiredCapacity) {
    changes.push({
      field: "requiredCapacity",
      materiality: "material",
      reason: "capacity_changed",
    });
  }

  if (!sameHelpModes(before.requiredHelpModes, after.requiredHelpModes)) {
    changes.push({
      field: "requiredHelpModes",
      materiality: "material",
      reason: "help_modes_changed",
    });
  }

  if (before.needType !== after.needType) {
    changes.push({ field: "needType", materiality: "material", reason: "need_type_changed" });
  }

  if (before.state !== after.state && after.state === "cancelled") {
    changes.push({ field: "state", materiality: "material", reason: "cancelled" });
  }

  if (before.importance !== after.importance) {
    changes.push({
      field: "importance",
      materiality: "cosmetic",
      reason: "reminder_threshold_changed",
    });
  }

  if (before.label !== after.label) {
    changes.push({ field: "label", materiality: "cosmetic", reason: "wording_changed" });
  }

  if (before.meetingDetail !== after.meetingDetail) {
    changes.push({ field: "meetingDetail", materiality: "cosmetic", reason: "note_changed" });
  }

  if (before.practicalNote !== after.practicalNote) {
    changes.push({ field: "practicalNote", materiality: "cosmetic", reason: "note_changed" });
  }

  return verdict(changes);
}

/**
 * Classify an edit to an event (plan §10.8, EVT-008).
 *
 * | Field | Verdict | Why |
 * |---|---|---|
 * | `startsAt` / `endsAt` | **material** | Plan §10.8: recalculate linked windows, reconfirm affected commitments |
 * | `locationLabel` / `locationPrivate` | **material** | Plan §10.8: reconfirm transport/accompany commitments — see {@link classifyEventChangeForNeed}, which narrows this |
 * | `title` | cosmetic | "Title wording only — usually no" |
 * | `shareTitle` | cosmetic | Plan §10.8 says explicitly: no commitment change (EVT-017) |
 * | `category` | cosmetic | Presentation |
 * | `practicalNote` | cosmetic | "Keep commitments; log change" |
 * | `state` → `cancelled` | **material** | Cancellation row |
 *
 * Note what is NOT here: a contact telephone number. The plan marks it
 * **Contextual**, and it is not a field on `EventRecord` today — when it arrives,
 * it classifies as `contextual` and the editor is asked. The type already
 * supports that verdict so the case cannot be quietly defaulted.
 */
export function classifyEventChange(
  before: EventChangeInput,
  after: EventChangeInput,
): ChangeClassification {
  const changes: FieldChange[] = [];

  if (before.startsAt !== after.startsAt || before.endsAt !== after.endsAt) {
    changes.push({
      field: before.startsAt !== after.startsAt ? "startsAt" : "endsAt",
      materiality: "material",
      reason: "event_time_moved",
    });
  }

  if (before.locationLabel !== after.locationLabel) {
    changes.push({ field: "locationLabel", materiality: "material", reason: "location_changed" });
  }

  if (before.locationPrivate !== after.locationPrivate) {
    changes.push({
      field: "locationPrivate",
      materiality: "material",
      reason: "location_changed",
    });
  }

  if (before.state !== after.state && after.state === "cancelled") {
    changes.push({ field: "state", materiality: "material", reason: "cancelled" });
  }

  if (before.title !== after.title) {
    changes.push({ field: "title", materiality: "cosmetic", reason: "wording_changed" });
  }

  if (before.shareTitle !== after.shareTitle) {
    changes.push({ field: "shareTitle", materiality: "cosmetic", reason: "share_title_changed" });
  }

  if (before.category !== after.category) {
    changes.push({ field: "category", materiality: "cosmetic", reason: "wording_changed" });
  }

  if (before.practicalNote !== after.practicalNote) {
    changes.push({ field: "practicalNote", materiality: "cosmetic", reason: "note_changed" });
  }

  return verdict(changes);
}

/**
 * The same event edit, judged for ONE linked need.
 *
 * Plan §10.8 does not say a location change reconfirms everybody — it says
 * "reconfirm **transport/accompany** commitments". Somebody who agreed to
 * telephone Ann after she gets home does not need to recheck because the
 * hospital's address was corrected, and asking them anyway is precisely the
 * noise that makes people stop reading these messages.
 *
 * So a location change is material only for needs that require being somewhere:
 * `driving` or `in_person`. A time change stays material for every linked need,
 * because a need's window is usually derived from the event's.
 */
export function classifyEventChangeForNeed(
  before: EventChangeInput,
  after: EventChangeInput,
  need: Pick<NeedChangeInput, "requiredHelpModes">,
): ChangeClassification {
  const eventLevel = classifyEventChange(before, after);
  const needsPresence = need.requiredHelpModes.some(
    (mode) => mode === "driving" || mode === "in_person",
  );

  if (needsPresence) return eventLevel;

  const narrowed = eventLevel.changes.map((change): FieldChange => {
    const isLocation = change.reason === "location_changed";
    return isLocation ? { ...change, materiality: "cosmetic" } : change;
  });

  return verdict(narrowed);
}

/**
 * Fold per-field verdicts into one outcome.
 *
 * Material wins over contextual, which wins over cosmetic. An edit that moves
 * the window AND fixes a typo is material — the typo does not dilute it.
 */
function verdict(changes: readonly FieldChange[]): ChangeClassification {
  if (changes.length === 0) return { outcome: "no_change", changes: [] };
  if (changes.some((c) => c.materiality === "material")) return { outcome: "material", changes };
  if (changes.some((c) => c.materiality === "contextual")) {
    return { outcome: "needs_editor_decision", changes };
  }
  return { outcome: "cosmetic", changes };
}

/**
 * Does this edit require a revision bump?
 *
 * Only material changes do. A cosmetic edit that bumped the revision would flip
 * every commitment to "Please recheck" via the coverage engine's staleness
 * check — the revision IS the reconfirmation trigger, so bumping it casually
 * would spam exactly the people the policy protects.
 */
export function requiresRevisionBump(classification: ChangeClassification): boolean {
  return classification.outcome === "material";
}

/** Order-independent comparison; help modes are a set, not a list. */
function sameHelpModes(a: readonly HelpMode[], b: readonly HelpMode[]): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return b.every((mode) => seen.has(mode));
}
